import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useChildMatches, useNavigate, useSearch } from '@tanstack/react-router';

import { type TriageCardRow } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { overviewRepository, triageInboxRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { AppIcons, EmptyHint, useOrgContext } from '@web/ui/core';
import { Button } from '@web/ui/core/atoms';
import { Scaffold } from '@web/ui/core/organisms';

import { TriageRow } from '../components/triage_row';
import { TriageTopBar } from '../components/triage_top_bar';
import { fireAndForget } from '@web/ui/core/utils';

export function TriagePage(): React.JSX.Element {
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const childMatches = useChildMatches();
  const { autoOpen } = useSearch({ strict: false });
  const query = useQuery({
    queryKey: queryKeys.triage.inbox(orgId),
    queryFn: () => triageInboxRepository.loadTriageCards(orgId),
  });
  const countsQuery = useQuery({
    queryKey: queryKeys.overview.sidebarCounts(orgId),
    queryFn: () => overviewRepository.loadSidebarCounts(orgId),
  });

  const rows: TriageCardRow[] = query.data ?? [];
  // Real backlog size — the rows list is capped server-side, so its length
  // undercounts. Same source (and cache entry) as the sidebar badge; never
  // below the visible list while the count is pending or stale.
  const total = Math.max(countsQuery.data?.triage ?? 0, rows.length);

  const activeCardId = (childMatches[0]?.params as { cardId?: string } | undefined)?.cardId ?? null;

  const openCard = (cardId: TriageCardRow['cardId']): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/triage/card/$cardId/$tab',
        params: { orgSlug, cardId, tab: 'conversation' },
      }),
    );
  };

  // Auto-open the first card on entry (and after an action returns us to the
  // bare index): redirect during render rather than via useEffect. O voltar do
  // card chega com `autoOpen: false` — reabrir aqui o devolveria a um card (o
  // próprio, quando ele é o topo da lista) e o voltar pareceria não funcionar.
  const firstCardId = rows[0]?.cardId;
  if (
    autoOpen !== false &&
    !query.isPending &&
    firstCardId !== undefined &&
    activeCardId === null
  ) {
    return (
      <Navigate
        to="/orgs/$orgSlug/triage/card/$cardId/$tab"
        params={{ orgSlug, cardId: firstCardId, tab: 'conversation' }}
        replace
      />
    );
  }

  return (
    <Scaffold
      appBar={<TriageTopBar total={total} />}
      body={
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <header className="flex flex-col gap-2">
            <span className="text-eyebrow inline-flex items-center gap-2">
              <span>{t('triage.eyebrow')}</span>
              <span aria-hidden className="text-faint-foreground">
                ·
              </span>
              <span>{t('triage.eyebrowLive')}</span>
            </span>
            <h1 className="text-display-lg font-serif font-normal m-0 leading-display tracking-display tabular-nums">
              {total === 0 ? (
                <>
                  {t('triage.title.empty.prefix')}{' '}
                  <em className="not-italic text-primary">{t('triage.title.empty.emphasis')}</em>.
                </>
              ) : (
                <>
                  <em className="not-italic text-primary">{total}</em>{' '}
                  {t('triage.title.cards', { count: total })}
                </>
              )}
            </h1>
            <p className="m-0 text-meta text-muted-foreground">{t('triage.inbox.sub')}</p>
          </header>
          <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-border-soft bg-surface">
            <div className="min-w-0 flex-1 overflow-y-auto">
              {activeCardId === null && rows.length > 0 ? (
                <EmptyHint testId="triage-no-card">{t('triage.inbox.noCardSelected')}</EmptyHint>
              ) : (
                <Outlet />
              )}
            </div>
            <aside className="flex w-triage-list shrink-0 flex-col overflow-y-auto border-l border-border-soft bg-paper">
              <TriageList
                query={query}
                rows={rows}
                activeCardId={activeCardId}
                onOpenCard={openCard}
              />
            </aside>
          </div>
        </div>
      }
    />
  );
}

function TriageList({
  query,
  rows,
  activeCardId,
  onOpenCard,
}: Readonly<{
  query: ReturnType<typeof useQuery<TriageCardRow[]>>;
  rows: TriageCardRow[];
  activeCardId: string | null;
  onOpenCard: (cardId: TriageCardRow['cardId']) => void;
}>): React.JSX.Element {
  if (query.isPending) {
    return (
      <ul className="flex flex-col" data-testid="triage-loading">
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="h-20 border-t border-border-soft first:border-t-0">
            <span className="mx-4 my-3.5 block h-full animate-pulse rounded-md bg-muted" />
          </li>
        ))}
      </ul>
    );
  }
  if (query.isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <AppIcons name="warning" size={28} className="text-tone-red-fg" />
        <p className="m-0 text-meta text-muted-foreground">{t('triage.error')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fireAndForget(query.refetch());
          }}
        >
          {t('triage.retry')}
        </Button>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <AppIcons name="check-circle" size={28} className="text-tone-green-fg" />
        <p className="m-0 text-card-title font-medium text-foreground">{t('triage.empty.title')}</p>
        <p className="m-0 max-w-prose-sm text-meta text-muted-foreground">
          {t('triage.empty.body')}
        </p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col pb-4">
      {rows.map((row) => (
        <TriageRow
          key={row.cardId}
          row={row}
          selected={row.cardId === activeCardId}
          onSelect={onOpenCard}
        />
      ))}
    </ul>
  );
}
